import {
  ChevronRightIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  TruckIcon,
  BanknotesIcon,
  ClockIcon,
  ClipboardDocumentCheckIcon,
  ArrowsRightLeftIcon,
  CodeBracketIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  EyeIcon,
} from '@heroicons/react/20/solid';
import { useId, useRef, useState, type ReactNode } from 'react';
import { OtsActionButton, OtsIconActionButton } from './OtsButton';
import { OtsFloatingPanel, type OtsFloatingAnchor } from './OtsFloatingPanel';
import type { OtsFloatingPlacement } from './ots-floating-position';
const defaultToolIcon = (label: string) => {
  const text = label.toLowerCase();
  const Icon = /compliance|validat/.test(text)
    ? ShieldCheckIcon
    : /audit/.test(text)
    ? ClipboardDocumentCheckIcon
    : /integration/.test(text)
    ? ArrowsRightLeftIcon
    : /coding/.test(text)
    ? CodeBracketIcon
    : /billing|revenue|cost|rate|invoice/.test(text)
    ? BanknotesIcon
    : /history/.test(text)
    ? ClockIcon
    : /download|export/.test(text)
    ? ArrowDownTrayIcon
    : /document/.test(text)
    ? DocumentTextIcon
    : /ticket|transport/.test(text)
    ? TruckIcon
    : /view|find/.test(text)
    ? EyeIcon
    : WrenchScrewdriverIcon;
  return <Icon aria-hidden="true" />;
};
export type OtsToolAction = {
  id: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
};
export type OtsToolGroup = {
  id: string;
  label: string;
  icon?: ReactNode;
  actions: OtsToolAction[];
};
export type OtsToolsMenuProps = {
  groups: OtsToolGroup[];
  anchor: OtsFloatingAnchor;
  onAction: (id: string) => void;
  onClose: () => void;
  label?: string;
  compact?: boolean;
  placement?: OtsFloatingPlacement;
  /**
   * `accordion` opens one section at a time, for long menus such as WaterGo's tools. `list` shows every section
   * under its heading with a divider between them, so everything is a glance away.
   */
  layout?: 'accordion' | 'list';
};
/** Grouped action launcher; callers supply applicable actions, including server-discovered compliance. */
export const OtsToolsMenu = ({
  groups,
  anchor,
  onAction,
  onClose,
  label = 'Tools',
  compact = true,
  placement,
  layout = 'accordion',
}: OtsToolsMenuProps) => {
  const visible = groups
    .map((g) => ({ ...g, actions: g.actions.filter((a) => !a.hidden) }))
    .filter((g) => g.actions.length);
  const [expanded, setExpanded] = useState<string | null>(
    () => visible[0]?.id ?? null
  );
  const id = useId();
  const toolButton = (action: OtsToolAction) => (
    <button
      key={action.id}
      type="button"
      disabled={action.disabled}
      onClick={() => onAction(action.id)}
      className={`flex w-full items-center gap-2 rounded-md px-2.5 text-left ${
        layout === 'list' ? 'min-h-8 py-1.5' : 'min-h-9 py-2'
      } ${
        compact ? 'text-xs' : 'text-sm'
      } hover:bg-[var(--ots-primary-container)] hover:text-[var(--ots-on-primary-container)] focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)] disabled:cursor-not-allowed disabled:opacity-50`}
    >
      <span aria-hidden="true" className="size-4 shrink-0 overflow-hidden">
        {action.icon ?? defaultToolIcon(action.label)}
      </span>
      <span className="min-w-0 flex-1 break-words">
        {action.label}
        {action.description && (
          <span className="mt-0.5 block text-[var(--ots-text-muted)]">
            {action.description}
          </span>
        )}
      </span>
    </button>
  );
  return (
    <OtsFloatingPanel
      anchor={anchor}
      onClose={onClose}
      label={label}
      compact={compact}
      placement={placement}
      onKeyDown={(event) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key))
          return;
        event.preventDefault();
        const buttons = Array.from(
          event.currentTarget.querySelectorAll<HTMLButtonElement>(
            'button:not(:disabled)'
          )
        ).filter((button) => !button.closest('[hidden]'));
        const current = buttons.indexOf(event.target as HTMLButtonElement);
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
            ? buttons.length - 1
            : event.key === 'ArrowUp'
            ? current < 0
              ? buttons.length - 1
              : (current - 1 + buttons.length) % buttons.length
            : (current + 1) % buttons.length;
        buttons[next]?.focus();
      }}
    >
      {visible.length && layout === 'list' ? (
        visible.map((group, index) => (
          <section
            key={group.id}
            aria-labelledby={`${id}-${group.id}`}
            className={index > 0 ? 'mt-1 border-t border-[var(--ots-outline-variant)] pt-1' : undefined}
          >
            {/* One section needs no heading; the menu's own name says what it is. */}
            <h3
              id={`${id}-${group.id}`}
              className={visible.length === 1 ? 'sr-only' : 'px-2.5 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]'}
            >
              {group.label}
            </h3>
            {group.actions.map((action) => toolButton(action))}
          </section>
        ))
      ) : visible.length ? (
        visible.map((group) => {
          const open = group.id === expanded;
          return (
            <section key={group.id}>
              <button
                type="button"
                aria-expanded={open}
                aria-controls={`${id}-${group.id}`}
                onClick={() => setExpanded(open ? null : group.id)}
                className="flex min-h-9 w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-[var(--ots-secondary)] hover:bg-[var(--ots-background)] focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]"
              >
                <span className="size-4 shrink-0">
                  {group.icon ?? defaultToolIcon(group.label)}
                </span>
                <span className="min-w-0 flex-1 break-words">
                  {group.label}
                </span>
                <ChevronRightIcon
                  aria-hidden="true"
                  className={`size-4 shrink-0 ${open ? 'rotate-90' : ''}`}
                />
              </button>
              <div
                id={`${id}-${group.id}`}
                hidden={!open}
                className="pb-1 pl-2"
              >
                {group.actions.map((action) => toolButton(action))}
              </div>
            </section>
          );
        })
      ) : (
        <p className="p-3 text-xs text-[var(--ots-text-muted)]">
          No tools are available for this selection.
        </p>
      )}
    </OtsFloatingPanel>
  );
};
/** React trigger counterpart to the external GWT and context-menu anchors. */
export const OtsToolsMenuButton = ({
  groups,
  onAction,
  compact = true,
  label = 'Tools',
  placement,
  layout,
  icon = <WrenchScrewdriverIcon />,
  iconOnly = false,
}: Omit<OtsToolsMenuProps, 'anchor' | 'onClose'> & {
  /** The button's icon; a wrench by default. */
  icon?: ReactNode;
  /** Just the icon, with the label as its name and tooltip, e.g. a More button in a row of icon actions. */
  iconOnly?: boolean;
}) => {
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((value) => !value);
  return (
    <>
      {iconOnly ? (
        <OtsIconActionButton
          ref={button}
          variant="text"
          size={compact ? 'compact' : 'default'}
          icon={icon}
          label={label}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={toggle}
        />
      ) : (
        <OtsActionButton
          ref={button}
          size={compact ? 'compact' : 'default'}
          icon={icon}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={toggle}
        >
          {label}
        </OtsActionButton>
      )}
      {open && button.current && (
        <OtsToolsMenu
          anchor={button.current}
          groups={groups}
          onAction={(id) => {
            setOpen(false);
            onAction(id);
          }}
          onClose={() => setOpen(false)}
          compact={compact}
          label={label}
          placement={placement}
          layout={layout}
        />
      )}
    </>
  );
};
